/**
 * RBAC Permissions Configuration
 * Organized by resource/module for easier management
 *
 * Legend:
 * - true     = Full access (CRUD)
 * - 'view'   = View only (read)
 * - 'own'    = Own data only (e.g., driver sees only their loads)
 * - false    = No access
 *
 * RBAC Rules:
 * 1. Cannot edit → hide the edit button in the UI
 * 2. Cannot view → disable in the API hooks (not in the screen UI).
 *    If a screen has multiple sections (e.g., Fleet + Driver), and the role
 *    cannot view Driver, the API hook returns no data — the UI renders
 *    naturally without needing per-section permission checks.
 * 3. Cannot delete → hide the delete button in the UI
 */

// =============================================================================
// TYPES
// =============================================================================

export type UserRole =
  | 'owner'
  | 'admin'
  | 'dispatcher'
  | 'driver'
  | 'accountant'
  | 'safety'
  | 'broker'
  | 'fuel_operator'
  | 'maintenance_operator'
  | 'maintenance_technician';

export type PermissionLevel = true | false | 'view' | 'own';

export interface ResourcePermissions {
  view: PermissionLevel;
  create: PermissionLevel;
  update: PermissionLevel;
  delete: PermissionLevel;
  [key: string]: PermissionLevel;
}

// =============================================================================
// ROLE DEFINITIONS
// =============================================================================

export const ROLE_DEFINITIONS: Record<UserRole, { name: string; description: string }> = {
  owner: {
    name: 'Owner',
    description: 'Organization owner — full access including billing & subscription',
  },
  admin: {
    name: 'Administrator',
    description: 'Administrator — full access except billing',
  },
  dispatcher: {
    name: 'Dispatcher',
    description: 'Operations — manage loads, dispatch, track drivers',
  },
  driver: {
    name: 'Driver',
    description: 'Driver — view own loads, settlements, paystubs, documents',
  },
  accountant: {
    name: 'Accountant',
    description: 'Finance — invoices, settlements, paystubs, reports, IFTA',
  },
  safety: {
    name: 'Safety Manager',
    description: 'Safety — compliance, drivers, fleet, maintenance (read-only)',
  },
  broker: {
    name: 'Broker',
    description: 'Brokerage — loads, dispatch, carriers, customers',
  },
  fuel_operator: {
    name: 'Fuel Operator',
    description: 'Fuel operations — view trucks, record fuel purchases',
  },
  maintenance_operator: {
    name: 'Maintenance Operator',
    description: 'Maintenance — records, work orders, parts, fleet, documents',
  },
  maintenance_technician: {
    name: 'Maintenance Technician',
    description: 'Technician — view fleet, read parts, update assigned work orders',
  },
};

// =============================================================================
// PERMISSIONS BY RESOURCE
// =============================================================================

/**
 * LOADS
 * Matrix: Owner=* Admin=* Dispatcher=RWM Driver=R Accountant=RWM Safety=— Broker=RWM FuelOp=— MaintOp=—
 */
export const LOAD_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, clone: true, assign: true, statusUpdate: true },
  admin: { view: true, create: true, update: true, delete: true, clone: true, assign: true, statusUpdate: true },
  dispatcher: { view: true, create: true, update: true, delete: false, clone: true, assign: true, statusUpdate: true },
  driver: { view: 'view', create: false, update: false, delete: false, clone: false, assign: false, statusUpdate: false },
  accountant: { view: true, create: true, update: true, delete: false, clone: true, assign: false, statusUpdate: true },
  safety: { view: false, create: false, update: false, delete: false, clone: false, assign: false, statusUpdate: false },
  broker: { view: true, create: true, update: true, delete: false, clone: true, assign: true, statusUpdate: true },
  fuel_operator: { view: false, create: false, update: false, delete: false, clone: false, assign: false, statusUpdate: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, clone: false, assign: false, statusUpdate: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, clone: false, assign: false, statusUpdate: false },
};

/**
 * DISPATCH
 * Matrix: Owner=* Admin=* Dispatcher=RWM Driver=— Accountant=— Safety=— Broker=RW FuelOp=— MaintOp=—
 */
export const DISPATCH_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, assign: true, statusUpdate: true },
  admin: { view: true, create: true, update: true, delete: true, assign: true, statusUpdate: true },
  dispatcher: { view: true, create: true, update: true, delete: false, assign: true, statusUpdate: true },
  driver: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  accountant: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  safety: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  broker: { view: true, create: true, update: true, delete: false, assign: false, statusUpdate: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
};

/**
 * STOPS
 */
export const STOP_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, reorder: true },
  admin: { view: true, create: true, update: true, delete: true, reorder: true },
  dispatcher: { view: true, create: true, update: true, delete: true, reorder: true },
  driver: { view: 'view', create: false, update: false, delete: false, reorder: false },
  accountant: { view: true, create: true, update: true, delete: false, reorder: true },
  safety: { view: false, create: false, update: false, delete: false, reorder: false },
  broker: { view: true, create: true, update: true, delete: true, reorder: true },
  fuel_operator: { view: false, create: false, update: false, delete: false, reorder: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, reorder: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, reorder: false },
};

/**
 * QUOTES
 */
export const QUOTE_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, send: true, convert: true },
  admin: { view: true, create: true, update: true, delete: true, send: true, convert: true },
  dispatcher: { view: true, create: true, update: true, delete: false, send: true, convert: true },
  driver: { view: false, create: false, update: false, delete: false, send: false, convert: false },
  accountant: { view: false, create: false, update: false, delete: false, send: false, convert: false },
  safety: { view: false, create: false, update: false, delete: false, send: false, convert: false },
  broker: { view: true, create: true, update: true, delete: false, send: true, convert: true },
  fuel_operator: { view: false, create: false, update: false, delete: false, send: false, convert: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, send: false, convert: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, send: false, convert: false },
};

/**
 * LOAD TEMPLATES
 */
export const TEMPLATE_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, use: true },
  admin: { view: true, create: true, update: true, delete: true, use: true },
  dispatcher: { view: true, create: false, update: false, delete: false, use: true },
  driver: { view: false, create: false, update: false, delete: false, use: false },
  accountant: { view: false, create: false, update: false, delete: false, use: false },
  safety: { view: false, create: false, update: false, delete: false, use: false },
  broker: { view: true, create: false, update: false, delete: false, use: true },
  fuel_operator: { view: false, create: false, update: false, delete: false, use: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, use: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, use: false },
};

/**
 * RECURRING LOADS
 */
export const RECURRING_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, generate: true, pauseResume: true },
  admin: { view: true, create: true, update: true, delete: true, generate: true, pauseResume: true },
  dispatcher: { view: true, create: false, update: false, delete: false, generate: true, pauseResume: false },
  driver: { view: false, create: false, update: false, delete: false, generate: false, pauseResume: false },
  accountant: { view: false, create: false, update: false, delete: false, generate: false, pauseResume: false },
  safety: { view: false, create: false, update: false, delete: false, generate: false, pauseResume: false },
  broker: { view: true, create: false, update: false, delete: false, generate: true, pauseResume: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, generate: false, pauseResume: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, generate: false, pauseResume: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, generate: false, pauseResume: false },
};

/**
 * DRIVERS
 * Matrix: Owner=* Admin=* Dispatcher=R Driver=— Accountant=— Safety=RW Broker=— FuelOp=— MaintOp=—
 */
export const DRIVER_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, viewLocation: true, viewHOS: true },
  admin: { view: true, create: true, update: true, delete: true, viewLocation: true, viewHOS: true },
  dispatcher: { view: 'view', create: false, update: false, delete: false, viewLocation: true, viewHOS: 'view' },
  driver: { view: false, create: false, update: false, delete: false, viewLocation: false, viewHOS: false },
  accountant: { view: false, create: false, update: false, delete: false, viewLocation: false, viewHOS: false },
  safety: { view: true, create: true, update: true, delete: false, viewLocation: false, viewHOS: true },
  broker: { view: false, create: false, update: false, delete: false, viewLocation: false, viewHOS: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, viewLocation: false, viewHOS: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, viewLocation: false, viewHOS: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, viewLocation: false, viewHOS: false },
};

/**
 * FLEET (Trucks & Trailers)
 * Matrix: Owner=* Admin=* Dispatcher=R Driver=— Accountant=— Safety=RW Broker=— FuelOp=R MaintOp=RW
 */
export const FLEET_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, dashboard: true },
  admin: { view: true, create: true, update: true, delete: true, dashboard: true },
  dispatcher: { view: 'view', create: false, update: false, delete: false, dashboard: 'view' },
  driver: { view: false, create: false, update: false, delete: false, dashboard: false },
  accountant: { view: false, create: false, update: false, delete: false, dashboard: false },
  safety: { view: true, create: true, update: true, delete: false, dashboard: true },
  broker: { view: false, create: false, update: false, delete: false, dashboard: false },
  fuel_operator: { view: 'view', create: false, update: false, delete: false, dashboard: false },
  maintenance_operator: { view: true, create: true, update: true, delete: false, dashboard: true },
  maintenance_technician: { view: true, create: false, update: false, delete: false, dashboard: false },
};

/**
 * CUSTOMERS
 * Matrix: Owner=* Admin=* Dispatcher=R Driver=— Accountant=— Safety=— Broker=RW FuelOp=— MaintOp=—
 */
export const CUSTOMER_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true },
  admin: { view: true, create: true, update: true, delete: true },
  dispatcher: { view: 'view', create: false, update: false, delete: false },
  driver: { view: false, create: false, update: false, delete: false },
  accountant: { view: false, create: false, update: false, delete: false },
  safety: { view: false, create: false, update: false, delete: false },
  broker: { view: true, create: true, update: true, delete: false },
  fuel_operator: { view: false, create: false, update: false, delete: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false },
};

/**
 * CARRIERS
 * Matrix: Owner=* Admin=* Dispatcher=— Driver=— Accountant=— Safety=— Broker=RWM FuelOp=— MaintOp=—
 */
export const CARRIER_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, verify: true, tender: true, viewPerformance: true },
  admin: { view: true, create: true, update: true, delete: true, verify: true, tender: true, viewPerformance: true },
  dispatcher: { view: false, create: false, update: false, delete: false, verify: false, tender: false, viewPerformance: false },
  driver: { view: false, create: false, update: false, delete: false, verify: false, tender: false, viewPerformance: false },
  accountant: { view: false, create: false, update: false, delete: false, verify: false, tender: false, viewPerformance: false },
  safety: { view: false, create: false, update: false, delete: false, verify: false, tender: false, viewPerformance: false },
  broker: { view: true, create: true, update: true, delete: false, verify: true, tender: true, viewPerformance: true },
  fuel_operator: { view: false, create: false, update: false, delete: false, verify: false, tender: false, viewPerformance: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, verify: false, tender: false, viewPerformance: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, verify: false, tender: false, viewPerformance: false },
};

/**
 * INVOICES & PAYMENTS
 * Matrix: Owner=* Admin=* Dispatcher=— Driver=— Accountant=RWM Safety=— Broker=— FuelOp=— MaintOp=—
 */
export const INVOICE_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, send: true, generateBatch: true, recordPayment: true },
  admin: { view: true, create: true, update: true, delete: true, send: true, generateBatch: true, recordPayment: true },
  dispatcher: { view: false, create: false, update: false, delete: false, send: false, generateBatch: false, recordPayment: false },
  driver: { view: false, create: false, update: false, delete: false, send: false, generateBatch: false, recordPayment: false },
  accountant: { view: true, create: true, update: true, delete: false, send: true, generateBatch: true, recordPayment: true },
  safety: { view: false, create: false, update: false, delete: false, send: false, generateBatch: false, recordPayment: false },
  broker: { view: false, create: false, update: false, delete: false, send: false, generateBatch: false, recordPayment: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, send: false, generateBatch: false, recordPayment: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, send: false, generateBatch: false, recordPayment: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, send: false, generateBatch: false, recordPayment: false },
};

/**
 * SETTLEMENTS
 * Matrix: Owner=* Admin=* Dispatcher=— Driver=R Accountant=RWM Safety=— Broker=— FuelOp=— MaintOp=—
 */
export const SETTLEMENT_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, approve: true, pay: true },
  admin: { view: true, create: true, update: true, delete: true, approve: true, pay: true },
  dispatcher: { view: false, create: false, update: false, delete: false, approve: false, pay: false },
  driver: { view: 'view', create: false, update: false, delete: false, approve: false, pay: false },
  accountant: { view: true, create: true, update: true, delete: false, approve: true, pay: true },
  safety: { view: false, create: false, update: false, delete: false, approve: false, pay: false },
  broker: { view: false, create: false, update: false, delete: false, approve: false, pay: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, approve: false, pay: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, approve: false, pay: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, approve: false, pay: false },
};

/**
 * PAYSTUBS
 * Matrix: Owner=* Admin=* Dispatcher=— Driver=R Accountant=RWDM Safety=— Broker=— FuelOp=— MaintOp=—
 */
export const PAYSTUB_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, approve: true, process: true },
  admin: { view: true, create: true, update: true, delete: true, approve: true, process: true },
  dispatcher: { view: false, create: false, update: false, delete: false, approve: false, process: false },
  driver: { view: 'view', create: false, update: false, delete: false, approve: false, process: false },
  accountant: { view: true, create: true, update: true, delete: true, approve: true, process: true },
  safety: { view: false, create: false, update: false, delete: false, approve: false, process: false },
  broker: { view: false, create: false, update: false, delete: false, approve: false, process: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, approve: false, process: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, approve: false, process: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, approve: false, process: false },
};

/**
 * EXPENSES / FINANCIALS
 * Matrix: Owner=* Admin=* Dispatcher=R Driver=— Accountant=RW Safety=— Broker=R FuelOp=RW MaintOp=—
 */
export const EXPENSE_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true },
  admin: { view: true, create: true, update: true, delete: true },
  dispatcher: { view: 'view', create: false, update: false, delete: false },
  driver: { view: false, create: false, update: false, delete: false },
  accountant: { view: true, create: true, update: true, delete: false },
  safety: { view: false, create: false, update: false, delete: false },
  broker: { view: 'view', create: false, update: false, delete: false },
  fuel_operator: { view: true, create: true, update: true, delete: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false },
};

/**
 * DOCUMENTS
 * Matrix: Owner=* Admin=* Dispatcher=RW Driver=RW Accountant=R Safety=RW Broker=R FuelOp=— MaintOp=RW
 */
export const DOCUMENT_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, generate: true, send: true },
  admin: { view: true, create: true, update: true, delete: true, generate: true, send: true },
  dispatcher: { view: true, create: true, update: true, delete: false, generate: true, send: true },
  driver: { view: true, create: true, update: true, delete: false, generate: false, send: false },
  accountant: { view: 'view', create: false, update: false, delete: false, generate: true, send: true },
  safety: { view: true, create: true, update: true, delete: false, generate: false, send: false },
  broker: { view: 'view', create: false, update: false, delete: false, generate: true, send: true },
  fuel_operator: { view: false, create: false, update: false, delete: false, generate: false, send: false },
  maintenance_operator: { view: true, create: true, update: true, delete: false, generate: false, send: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, generate: false, send: false },
};

/**
 * MAINTENANCE (Records)
 * Matrix: Owner=* Admin=* Dispatcher=— Driver=— Accountant=— Safety=R Broker=— FuelOp=— MaintOp=RW
 */
export const MAINTENANCE_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true },
  admin: { view: true, create: true, update: true, delete: true },
  dispatcher: { view: false, create: false, update: false, delete: false },
  driver: { view: false, create: false, update: false, delete: false },
  accountant: { view: false, create: false, update: false, delete: false },
  safety: { view: 'view', create: false, update: false, delete: false },
  broker: { view: false, create: false, update: false, delete: false },
  fuel_operator: { view: false, create: false, update: false, delete: false },
  maintenance_operator: { view: true, create: true, update: true, delete: false },
  maintenance_technician: { view: true, create: false, update: false, delete: false },
};

/**
 * WORK ORDERS
 * Matrix: Owner=* Admin=* Dispatcher=— Driver=— Accountant=— Safety=R Broker=— FuelOp=— MaintOp=RW
 */
export const WORK_ORDER_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, assign: true, statusUpdate: true },
  admin: { view: true, create: true, update: true, delete: true, assign: true, statusUpdate: true },
  dispatcher: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  driver: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  accountant: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  safety: { view: 'view', create: false, update: false, delete: false, assign: false, statusUpdate: false },
  broker: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, assign: false, statusUpdate: false },
  maintenance_operator: { view: true, create: true, update: true, delete: false, assign: true, statusUpdate: true },
  maintenance_technician: { view: true, create: false, update: false, delete: false, assign: false, statusUpdate: true },
};

/**
 * PARTS
 * Matrix: Owner=* Admin=* Dispatcher=— Driver=— Accountant=— Safety=— Broker=— FuelOp=— MaintOp=RW
 */
export const PARTS_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true },
  admin: { view: true, create: true, update: true, delete: true },
  dispatcher: { view: false, create: false, update: false, delete: false },
  driver: { view: false, create: false, update: false, delete: false },
  accountant: { view: false, create: false, update: false, delete: false },
  safety: { view: false, create: false, update: false, delete: false },
  broker: { view: false, create: false, update: false, delete: false },
  fuel_operator: { view: false, create: false, update: false, delete: false },
  maintenance_operator: { view: true, create: true, update: true, delete: false },
  maintenance_technician: { view: true, create: false, update: false, delete: false },
};

/**
 * INSPECTIONS (DVIR)
 */
export const INSPECTION_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, resolveDefects: true },
  admin: { view: true, create: true, update: true, delete: true, resolveDefects: true },
  dispatcher: { view: false, create: false, update: false, delete: false, resolveDefects: false },
  driver: { view: false, create: false, update: false, delete: false, resolveDefects: false },
  accountant: { view: false, create: false, update: false, delete: false, resolveDefects: false },
  safety: { view: true, create: true, update: true, delete: false, resolveDefects: true },
  broker: { view: false, create: false, update: false, delete: false, resolveDefects: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, resolveDefects: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, resolveDefects: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, resolveDefects: false },
};

/**
 * COMPLIANCE
 * Matrix: Owner=* Admin=* Dispatcher=— Driver=— Accountant=— Safety=RWM Broker=— FuelOp=— MaintOp=—
 */
export const COMPLIANCE_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true },
  admin: { view: true, create: true, update: true, delete: true },
  dispatcher: { view: false, create: false, update: false, delete: false },
  driver: { view: false, create: false, update: false, delete: false },
  accountant: { view: false, create: false, update: false, delete: false },
  safety: { view: true, create: true, update: true, delete: false },
  broker: { view: false, create: false, update: false, delete: false },
  fuel_operator: { view: false, create: false, update: false, delete: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false },
};

/**
 * REPORTS & ANALYTICS
 * Matrix: Owner=* Admin=* Dispatcher=— Driver=— Accountant=RWM Safety=— Broker=— FuelOp=— MaintOp=—
 */
export const REPORT_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, export: true, ifta: true },
  admin: { view: true, create: true, update: true, delete: true, export: true, ifta: true },
  dispatcher: { view: false, create: false, update: false, delete: false, export: false, ifta: false },
  driver: { view: false, create: false, update: false, delete: false, export: false, ifta: false },
  accountant: { view: true, create: true, update: true, delete: false, export: true, ifta: true },
  safety: { view: false, create: false, update: false, delete: false, export: false, ifta: false },
  broker: { view: false, create: false, update: false, delete: false, export: false, ifta: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, export: false, ifta: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, export: false, ifta: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, export: false, ifta: false },
};

/**
 * COLLECTIONS
 */
export const COLLECTION_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, logActivity: true, generateLetter: true },
  admin: { view: true, create: true, update: true, delete: true, logActivity: true, generateLetter: true },
  dispatcher: { view: false, create: false, update: false, delete: false, logActivity: false, generateLetter: false },
  driver: { view: false, create: false, update: false, delete: false, logActivity: false, generateLetter: false },
  accountant: { view: true, create: true, update: true, delete: false, logActivity: true, generateLetter: true },
  safety: { view: false, create: false, update: false, delete: false, logActivity: false, generateLetter: false },
  broker: { view: false, create: false, update: false, delete: false, logActivity: false, generateLetter: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, logActivity: false, generateLetter: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, logActivity: false, generateLetter: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, logActivity: false, generateLetter: false },
};

/**
 * SETTINGS & ADMINISTRATION
 * Matrix: Owner=RW Admin=RW, others=—
 */
export const SETTINGS_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, manageUsers: true, manageBilling: true, manageIntegrations: true, viewAuditLogs: true, manageApiKeys: true },
  admin: { view: true, create: true, update: true, delete: true, manageUsers: true, manageBilling: false, manageIntegrations: true, viewAuditLogs: true, manageApiKeys: true },
  dispatcher: { view: false, create: false, update: false, delete: false, manageUsers: false, manageBilling: false, manageIntegrations: false, viewAuditLogs: false, manageApiKeys: false },
  driver: { view: false, create: false, update: false, delete: false, manageUsers: false, manageBilling: false, manageIntegrations: false, viewAuditLogs: false, manageApiKeys: false },
  accountant: { view: false, create: false, update: false, delete: false, manageUsers: false, manageBilling: false, manageIntegrations: false, viewAuditLogs: false, manageApiKeys: false },
  safety: { view: false, create: false, update: false, delete: false, manageUsers: false, manageBilling: false, manageIntegrations: false, viewAuditLogs: false, manageApiKeys: false },
  broker: { view: false, create: false, update: false, delete: false, manageUsers: false, manageBilling: false, manageIntegrations: false, viewAuditLogs: false, manageApiKeys: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, manageUsers: false, manageBilling: false, manageIntegrations: false, viewAuditLogs: false, manageApiKeys: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, manageUsers: false, manageBilling: false, manageIntegrations: false, viewAuditLogs: false, manageApiKeys: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, manageUsers: false, manageBilling: false, manageIntegrations: false, viewAuditLogs: false, manageApiKeys: false },
};

/**
 * ORGANIZATION
 * Matrix: Owner=RWD Admin=RW, others=—
 */
export const ORGANIZATION_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true },
  admin: { view: true, create: false, update: true, delete: false },
  dispatcher: { view: false, create: false, update: false, delete: false },
  driver: { view: false, create: false, update: false, delete: false },
  accountant: { view: false, create: false, update: false, delete: false },
  safety: { view: false, create: false, update: false, delete: false },
  broker: { view: false, create: false, update: false, delete: false },
  fuel_operator: { view: false, create: false, update: false, delete: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false },
};

/**
 * MEMBERS
 * Matrix: Owner=RWDI Admin=RWI, others=—
 */
export const MEMBER_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true, invite: true },
  admin: { view: true, create: true, update: true, delete: false, invite: true },
  dispatcher: { view: false, create: false, update: false, delete: false, invite: false },
  driver: { view: false, create: false, update: false, delete: false, invite: false },
  accountant: { view: false, create: false, update: false, delete: false, invite: false },
  safety: { view: false, create: false, update: false, delete: false, invite: false },
  broker: { view: false, create: false, update: false, delete: false, invite: false },
  fuel_operator: { view: false, create: false, update: false, delete: false, invite: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false, invite: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false, invite: false },
};

/**
 * INTEGRATIONS
 * Matrix: Owner=* Admin=*, others=—
 */
export const INTEGRATION_PERMISSIONS: Record<UserRole, ResourcePermissions> = {
  owner: { view: true, create: true, update: true, delete: true },
  admin: { view: true, create: true, update: true, delete: true },
  dispatcher: { view: false, create: false, update: false, delete: false },
  driver: { view: false, create: false, update: false, delete: false },
  accountant: { view: false, create: false, update: false, delete: false },
  safety: { view: false, create: false, update: false, delete: false },
  broker: { view: false, create: false, update: false, delete: false },
  fuel_operator: { view: false, create: false, update: false, delete: false },
  maintenance_operator: { view: false, create: false, update: false, delete: false },
  maintenance_technician: { view: false, create: false, update: false, delete: false },
};

// =============================================================================
// DATA VISIBILITY
// =============================================================================

export interface DataVisibilityConfig {
  revenue: boolean;
  costs: boolean;
  margins: boolean;
  settlements: boolean;
  customerRates: boolean;
  driverPay: boolean;
  companyFinancials: boolean;
}

export const DATA_VISIBILITY: Record<UserRole, DataVisibilityConfig> = {
  owner: {
    revenue: true,
    costs: true,
    margins: true,
    settlements: true,
    customerRates: true,
    driverPay: true,
    companyFinancials: true,
  },
  admin: {
    revenue: true,
    costs: true,
    margins: true,
    settlements: true,
    customerRates: true,
    driverPay: true,
    companyFinancials: true,
  },
  dispatcher: {
    revenue: true,
    costs: false,
    margins: false,
    settlements: false,
    customerRates: true,
    driverPay: false,
    companyFinancials: false,
  },
  driver: {
    revenue: false,
    costs: false,
    margins: false,
    settlements: true,  // Own only
    customerRates: false,
    driverPay: true,    // Own only
    companyFinancials: false,
  },
  accountant: {
    revenue: true,
    costs: true,
    margins: true,
    settlements: true,
    customerRates: true,
    driverPay: true,
    companyFinancials: true,
  },
  safety: {
    revenue: false,
    costs: false,
    margins: false,
    settlements: false,
    customerRates: false,
    driverPay: false,
    companyFinancials: false,
  },
  broker: {
    revenue: true,
    costs: true,
    margins: true,
    settlements: false,
    customerRates: true,
    driverPay: false,
    companyFinancials: false,
  },
  fuel_operator: {
    revenue: false,
    costs: true,
    margins: false,
    settlements: false,
    customerRates: false,
    driverPay: false,
    companyFinancials: false,
  },
  maintenance_operator: {
    revenue: false,
    costs: false,
    margins: false,
    settlements: false,
    customerRates: false,
    driverPay: false,
    companyFinancials: false,
  },
  maintenance_technician: {
    revenue: false,
    costs: false,
    margins: false,
    settlements: false,
    customerRates: false,
    driverPay: false,
    companyFinancials: false,
  },
};

// =============================================================================
// NAVIGATION VISIBILITY
// =============================================================================

export type NavCategory =
  | 'dashboard'
  | 'loads'
  | 'dispatch'
  | 'drivers'
  | 'fleet'
  | 'customers'
  | 'carriers'
  | 'invoices'
  | 'settlements'
  | 'paystubs'
  | 'expenses'
  | 'compliance'
  | 'documents'
  | 'reports'
  | 'analytics'
  | 'settings'
  | 'messaging'
  | 'tracking'
  | 'marketplace'
  | 'integrations'
  | 'ifta'
  | 'collections'
  | 'lanes'
  | 'margins'
  | 'edi'
  | 'portal'
  | 'inspections'
  | 'maintenance'
  | 'workOrders'
  | 'parts';

export interface NavItemConfig {
  visible: boolean;
  readonly?: boolean;
}

export const NAV_VISIBILITY: Record<UserRole, Partial<Record<NavCategory, NavItemConfig>>> = {
  owner: {
    dashboard: { visible: true },
    loads: { visible: true },
    dispatch: { visible: true },
    drivers: { visible: true },
    fleet: { visible: true },
    customers: { visible: true },
    carriers: { visible: true },
    invoices: { visible: true },
    settlements: { visible: true },
    paystubs: { visible: true },
    expenses: { visible: true },
    compliance: { visible: true },
    documents: { visible: true },
    reports: { visible: true },
    analytics: { visible: true },
    settings: { visible: true },
    messaging: { visible: true },
    tracking: { visible: true },
    marketplace: { visible: true },
    integrations: { visible: true },
    ifta: { visible: true },
    collections: { visible: true },
    lanes: { visible: true },
    margins: { visible: true },
    edi: { visible: true },
    portal: { visible: true },
    inspections: { visible: true },
    maintenance: { visible: true },
    workOrders: { visible: true },
    parts: { visible: true },
  },
  admin: {
    dashboard: { visible: true },
    loads: { visible: true },
    dispatch: { visible: true },
    drivers: { visible: true },
    fleet: { visible: true },
    customers: { visible: true },
    carriers: { visible: true },
    invoices: { visible: true },
    settlements: { visible: true },
    paystubs: { visible: true },
    expenses: { visible: true },
    compliance: { visible: true },
    documents: { visible: true },
    reports: { visible: true },
    analytics: { visible: true },
    settings: { visible: true },
    messaging: { visible: true },
    tracking: { visible: true },
    marketplace: { visible: true },
    integrations: { visible: true },
    ifta: { visible: true },
    collections: { visible: true },
    lanes: { visible: true },
    margins: { visible: true },
    edi: { visible: true },
    portal: { visible: true },
    inspections: { visible: true },
    maintenance: { visible: true },
    workOrders: { visible: true },
    parts: { visible: true },
  },
  dispatcher: {
    dashboard: { visible: true },
    loads: { visible: true },
    dispatch: { visible: true },
    drivers: { visible: true, readonly: true },
    fleet: { visible: true, readonly: true },
    customers: { visible: true, readonly: true },
    documents: { visible: true },
    expenses: { visible: true, readonly: true },
    messaging: { visible: true },
    tracking: { visible: true },
  },
  driver: {
    dashboard: { visible: true },
    loads: { visible: true, readonly: true },
    documents: { visible: true },
    messaging: { visible: true },
    settlements: { visible: true, readonly: true },
    paystubs: { visible: true, readonly: true },
  },
  accountant: {
    dashboard: { visible: true },
    loads: { visible: true },
    invoices: { visible: true },
    settlements: { visible: true },
    paystubs: { visible: true },
    expenses: { visible: true },
    documents: { visible: true, readonly: true },
    reports: { visible: true },
    analytics: { visible: true },
    ifta: { visible: true },
    collections: { visible: true },
  },
  safety: {
    dashboard: { visible: true },
    drivers: { visible: true },
    fleet: { visible: true },
    compliance: { visible: true },
    documents: { visible: true },
    maintenance: { visible: true, readonly: true },
    workOrders: { visible: true, readonly: true },
  },
  broker: {
    dashboard: { visible: true },
    loads: { visible: true },
    dispatch: { visible: true },
    customers: { visible: true },
    carriers: { visible: true },
    documents: { visible: true, readonly: true },
    expenses: { visible: true, readonly: true },
    messaging: { visible: true },
    tracking: { visible: true },
    marketplace: { visible: true },
    lanes: { visible: true },
  },
  fuel_operator: {
    dashboard: { visible: true },
    fleet: { visible: true, readonly: true },  // View trucks only
    expenses: { visible: true },               // Fuel charges — create & edit
  },
  maintenance_operator: {
    dashboard: { visible: true },
    fleet: { visible: true },
    maintenance: { visible: true },
    workOrders: { visible: true },
    parts: { visible: true },
    documents: { visible: true },
  },
  maintenance_technician: {
    workOrders: { visible: true },
    parts: { visible: true, readonly: true },
  },
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Check if a role has permission for an action on a resource
 */
export function hasPermission(
  role: UserRole,
  resource: string,
  action: string
): PermissionLevel {
  const permissionMaps: Record<string, Record<UserRole, ResourcePermissions>> = {
    loads: LOAD_PERMISSIONS,
    dispatch: DISPATCH_PERMISSIONS,
    stops: STOP_PERMISSIONS,
    quotes: QUOTE_PERMISSIONS,
    templates: TEMPLATE_PERMISSIONS,
    recurring: RECURRING_PERMISSIONS,
    drivers: DRIVER_PERMISSIONS,
    fleet: FLEET_PERMISSIONS,
    customers: CUSTOMER_PERMISSIONS,
    carriers: CARRIER_PERMISSIONS,
    invoices: INVOICE_PERMISSIONS,
    settlements: SETTLEMENT_PERMISSIONS,
    paystubs: PAYSTUB_PERMISSIONS,
    expenses: EXPENSE_PERMISSIONS,
    documents: DOCUMENT_PERMISSIONS,
    maintenance: MAINTENANCE_PERMISSIONS,
    work_orders: WORK_ORDER_PERMISSIONS,
    parts: PARTS_PERMISSIONS,
    inspections: INSPECTION_PERMISSIONS,
    compliance: COMPLIANCE_PERMISSIONS,
    reports: REPORT_PERMISSIONS,
    collections: COLLECTION_PERMISSIONS,
    settings: SETTINGS_PERMISSIONS,
    organization: ORGANIZATION_PERMISSIONS,
    members: MEMBER_PERMISSIONS,
    integrations: INTEGRATION_PERMISSIONS,
  };

  const resourcePerms = permissionMaps[resource];
  if (!resourcePerms) return false;
  const rolePerms = resourcePerms[role];
  if (!rolePerms) return false;
  return rolePerms[action] ?? false;
}

/**
 * Check if a role can view a navigation item
 */
export function canViewNav(role: UserRole, category: NavCategory): boolean {
  return NAV_VISIBILITY[role]?.[category]?.visible ?? false;
}

/**
 * Check if a navigation item is readonly for a role
 */
export function isNavReadonly(role: UserRole, category: NavCategory): boolean {
  return NAV_VISIBILITY[role]?.[category]?.readonly ?? false;
}

/**
 * Check if a role can view specific data type
 */
export function canViewData(role: UserRole, dataType: keyof DataVisibilityConfig): boolean {
  return DATA_VISIBILITY[role]?.[dataType] ?? false;
}

/**
 * Check if role is admin level (owner or admin)
 */
export function isAdminRole(role: UserRole): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Check if role can manage billing
 */
export function canManageBilling(role: UserRole): boolean {
  return role === 'owner';
}

/**
 * Check if role can manage users
 */
export function canManageUsers(role: UserRole): boolean {
  return role === 'owner' || role === 'admin';
}

/**
 * Get role display info
 */
export function getRoleInfo(role: UserRole) {
  return ROLE_DEFINITIONS[role] ?? { name: role, description: '' };
}

/**
 * Get all permissions for a role on a resource
 */
export function getResourcePermissions(
  role: UserRole,
  resource: string
): ResourcePermissions | null {
  const permissionMaps: Record<string, Record<UserRole, ResourcePermissions>> = {
    loads: LOAD_PERMISSIONS,
    dispatch: DISPATCH_PERMISSIONS,
    stops: STOP_PERMISSIONS,
    quotes: QUOTE_PERMISSIONS,
    templates: TEMPLATE_PERMISSIONS,
    recurring: RECURRING_PERMISSIONS,
    drivers: DRIVER_PERMISSIONS,
    fleet: FLEET_PERMISSIONS,
    customers: CUSTOMER_PERMISSIONS,
    carriers: CARRIER_PERMISSIONS,
    invoices: INVOICE_PERMISSIONS,
    settlements: SETTLEMENT_PERMISSIONS,
    paystubs: PAYSTUB_PERMISSIONS,
    expenses: EXPENSE_PERMISSIONS,
    documents: DOCUMENT_PERMISSIONS,
    maintenance: MAINTENANCE_PERMISSIONS,
    work_orders: WORK_ORDER_PERMISSIONS,
    parts: PARTS_PERMISSIONS,
    inspections: INSPECTION_PERMISSIONS,
    compliance: COMPLIANCE_PERMISSIONS,
    reports: REPORT_PERMISSIONS,
    collections: COLLECTION_PERMISSIONS,
    settings: SETTINGS_PERMISSIONS,
    organization: ORGANIZATION_PERMISSIONS,
    members: MEMBER_PERMISSIONS,
    integrations: INTEGRATION_PERMISSIONS,
    ifta: REPORT_PERMISSIONS
  };

  return permissionMaps[resource]?.[role] ?? null;
}
